import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../components/AuthContext';
import { 
  FaPlus, 
  FaMinus, 
  FaSearch, 
  FaTimes, 
  FaEye, 
  FaFilter, 
  FaDownload,
  FaBox,
  FaCalendarAlt,
  FaSave,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaStickyNote,
  FaTrashAlt,
  FaUndo,
  FaBoxes,
  FaCheck,
  FaBan,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaUser,
  FaCube
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Select from 'react-select';

const DemolishedTiles = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([{
    tileId: null,
    tileName: '',
    tileDetails: null,
    demolishedDamagedBoxes: 0,
    demolishedDamagedPieces: 0,
    reason: '',
    availableFullDamagedBox: 0,
    availableFullDamagedPieces: 0
  }]);

  const [showTilesModal, setShowTilesModal] = useState(false);
  const [tilesSearch, setTilesSearch] = useState('');
  const [tilesPage, setTilesPage] = useState(1);
  const [tilesLimit] = useState(10);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [selectedTileIndex, setSelectedTileIndex] = useState(0);
  
  // History state
  const [demolishHistory, setDemolishHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    location: null,
    startDate: null,
    endDate: null,
    status: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');

  // Approval state
  const [approving, setApproving] = useState(false);

  // Refs
  const searchInputRef = useRef(null);
  const boxRefs = useRef([]);

  // Initial data loading
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load history when filters or pagination changes
  useEffect(() => {
    fetchDemolishHistory();
  }, [historyPage, historyFilters]);

  // Focus search when modal opens
  useEffect(() => {
    if (showTilesModal && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showTilesModal]);

  // Update available stock when location changes
  useEffect(() => {
    if (selectedLocation) {
      const updatedItems = items.map(item => {
        if (item.tileId) {
          const { boxes, pieces } = getAvailableFullDamaged(item.tileId, selectedLocation.value);
          return { ...item, availableFullDamagedBox: boxes, availableFullDamagedPieces: pieces };
        }
        return item;
      });
      setItems(updatedItems);
    }
  }, [selectedLocation]);

  // Update refs when items change
  useEffect(() => {
    boxRefs.current = boxRefs.current.slice(0, items.length);
  }, [items.length]);

  // Filter and paginate tiles for modal
  const filteredTiles = tiles.filter(tile => {
    const term = (tilesSearch || '').toLowerCase();
    return (
      tile.tilesName?.toLowerCase().includes(term) ||
      tile.displayName?.toLowerCase().includes(term) ||
      tile.id?.toLowerCase().includes(term) ||
      tile.brand?.name?.toLowerCase().includes(term) ||
      tile.tilesCategory?.name?.toLowerCase().includes(term)
    );
  });

  const paginatedTiles = filteredTiles.slice(
    (tilesPage - 1) * tilesLimit,
    tilesPage * tilesLimit
  );

  const totalTilesPages = Math.ceil(filteredTiles.length / tilesLimit);

  useEffect(() => {
    setSelectedTileIndex(0);
  }, [tilesSearch, tilesPage]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const [tilesRes, locationsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles?userId=${user.user._id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (tilesRes.ok) {
        const data = await tilesRes.json();
        setTiles(Array.isArray(data) ? data : data.data || []);
      }

      if (locationsRes.ok) {
        const data = await locationsRes.json();
        setLocations(Array.isArray(data) ? data : data.data || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load initial data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDemolishHistory = async () => {
    setHistoryLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        ...(historyFilters.location && { location: historyFilters.location }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate }),
        ...(historyFilters.status && { status: historyFilters.status })
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/demolished-tiles?${queryParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();

      if (data.success) {
        setDemolishHistory(data.data?.demolitions || []);
        setTotalHistory(data.data?.pagination?.totalItems || 0);
        setTotalPages(data.data?.pagination?.totalPages || 0);
      } else {
        setDemolishHistory([]);
        setTotalHistory(0);
      }
    } catch (error) {
      console.error('Error fetching demolish history:', error);
      setDemolishHistory([]);
      setTotalHistory(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getAvailableFullDamaged = (tileId, locationId) => {
    const tile = tiles.find(t => t._id === tileId);
    if (!tile || !tile.stock) return { boxes: 0, pieces: 0 };
    const locationStock = tile.stock.find(s => 
      (s.location._id || s.location) === locationId || s.location._id === locationId || s.location === locationId
    );
    return {
      boxes: locationStock?.fullDamagedBox || 0,
      pieces: locationStock?.fullDamagedPieces || 0
    };
  };

  const handleTileSelect = (tile, index) => {
    if (!selectedLocation) {
      Swal.fire({ icon: 'warning', title: 'Select Location', text: 'Please select a location first' });
      return;
    }

    // Check duplicate
    const existingIndex = items.findIndex(item => item.tileId === tile._id);
    if (existingIndex !== -1 && existingIndex !== index) {
      Swal.fire({ icon: 'warning', title: 'Tile Already Added', text: 'This tile is already in the list' });
      return;
    }

    const { boxes, pieces } = getAvailableFullDamaged(tile._id, selectedLocation.value);
    
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      tileId: tile._id,
      tileName: tile.tilesName || tile.displayName,
      tileDetails: tile,
      availableFullDamagedBox: boxes,
      availableFullDamagedPieces: pieces
    };
    setItems(updatedItems);
    setShowTilesModal(false);
    setCurrentItemIndex(null);
    setTilesSearch('');
    setSelectedTileIndex(0);

    setTimeout(() => boxRefs.current[index]?.focus(), 100);
  };

  const addItem = () => {
    setItems([...items, {
      tileId: null,
      tileName: '',
      tileDetails: null,
      demolishedDamagedBoxes: 0,
      demolishedDamagedPieces: 0,
      reason: '',
      availableFullDamagedBox: 0,
      availableFullDamagedPieces: 0
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    if (field === 'demolishedDamagedBoxes') {
      const numValue = Math.max(0, parseInt(value) || 0);
      if (numValue > updatedItems[index].availableFullDamagedBox) {
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Stock',
          text: `Available full damaged boxes: ${updatedItems[index].availableFullDamagedBox}`
        });
        return;
      }
      updatedItems[index][field] = numValue;
    } else if (field === 'demolishedDamagedPieces') {
      const numValue = Math.max(0, parseInt(value) || 0);
      if (numValue > updatedItems[index].availableFullDamagedPieces) {
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Stock',
          text: `Available full damaged pieces: ${updatedItems[index].availableFullDamagedPieces}`
        });
        return;
      }
      updatedItems[index][field] = numValue;
    } else {
      updatedItems[index][field] = value;
    }
    setItems(updatedItems);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (index === items.length - 1) {
        addItem();
        setTimeout(() => boxRefs.current[index + 1]?.focus(), 0);
      } else {
        boxRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowTilesModal(false);
      return;
    }
    const count = paginatedTiles.length;
    if (count === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedTileIndex(prev => prev < count - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedTileIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = paginatedTiles[selectedTileIndex];
      if (selected && currentItemIndex !== null) {
        handleTileSelect(selected, currentItemIndex);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select a location' });
      return;
    }

    if (!reason.trim()) {
      Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please provide a reason for demolition' });
      return;
    }

    const validItems = items.filter(item => item.tileId && (item.demolishedDamagedBoxes > 0 || item.demolishedDamagedPieces > 0));

    if (validItems.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Valid Items', text: 'Please add at least one tile with full damaged quantity' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        location: selectedLocation.value,
        demolitionDate: selectedDate,
        reason,
        remarks,
        items: validItems.map(item => ({
          tileId: item.tileId,
          demolishedDamagedBoxes: item.demolishedDamagedBoxes,
          demolishedDamagedPieces: item.demolishedDamagedPieces,
          reason: item.reason
        }))
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/demolished-tiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'Success', text: 'Demolition entry created (pending approval)' });
        
        // Reset form
        setItems([{
          tileId: null, tileName: '', tileDetails: null,
          demolishedDamagedBoxes: 0, demolishedDamagedPieces: 0, reason: '',
          availableFullDamagedBox: 0, availableFullDamagedPieces: 0
        }]);
        setReason('');
        setRemarks('');
        
        fetchDemolishHistory();
        setActiveTab('history');
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to create demolition entry' });
      }
    } catch (error) {
      console.error('Error creating demolition entry:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create demolition entry' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (entryId, status) => {
    const action = status === 'approved' ? 'approve' : 'reject';
    
    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Entry?`,
      text: status === 'approved' 
        ? 'This will deduct the full damaged stock. This action cannot be undone.' 
        : 'This will reject the demolition entry.',
      icon: status === 'approved' ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonColor: status === 'approved' ? '#16a34a' : '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${action} it`,
      input: 'textarea',
      inputLabel: 'Remarks (optional)',
      inputPlaceholder: 'Enter approval remarks...',
      inputAttributes: { 'aria-label': 'Approval remarks' }
    });

    if (!result.isConfirmed) return;

    setApproving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/demolished-tiles/${entryId}/approval`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status,
          approvalRemarks: result.value || ''
        })
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'Success', text: `Entry ${status} successfully` });
        fetchDemolishHistory();
        if (showDetailsModal) {
          setSelectedEntry(prev => prev ? { ...prev, status } : null);
          setShowDetailsModal(false);
        }
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: data.message || `Failed to ${action} entry` });
      }
    } catch (error) {
      console.error(`Error ${action}ing entry:`, error);
      Swal.fire({ icon: 'error', title: 'Error', text: `Failed to ${action} entry` });
    } finally {
      setApproving(false);
    }
  };

  const handleViewDetails = async (entry) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/demolished-tiles/${entry._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setSelectedEntry(data.data);
      } else {
        setSelectedEntry(entry);
      }
    } catch {
      setSelectedEntry(entry);
    }
    setShowDetailsModal(true);
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({ ...prev, [field]: value }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({ location: null, startDate: null, endDate: null, status: null });
    setHistoryPage(1);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <FaClock className="mr-1" />, darkBg: 'bg-yellow-900/30', darkText: 'text-yellow-300' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: <FaCheckCircle className="mr-1" />, darkBg: 'bg-green-900/30', darkText: 'text-green-300' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: <FaTimesCircle className="mr-1" />, darkBg: 'bg-red-900/30', darkText: 'text-red-300' }
    };
    const c = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${darkMode ? `${c.darkBg} ${c.darkText}` : `${c.bg} ${c.text}`}`}>
        {c.icon} {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Demolished ID', 'Date', 'Location', 'Status', 'Tile Name', 'Tile ID', 'Full Dmg Boxes', 'Full Dmg Pieces', 'Item Reason', 'Overall Reason', 'Created By', 'Remarks'],
      ...demolishHistory.flatMap(entry =>
        (entry.items || []).map(item => [
          entry.demolishedId || 'N/A',
          formatDate(entry.demolishedDate),
          entry.location?.name || 'N/A',
          entry.status || 'N/A',
          item.tileId?.tilesName || item.tileId?.displayName || 'N/A',
          item.tileId?.partNumber || 'N/A',
          item.demolishedDamagedBoxes || 0,
          item.demolishedDamagedPieces || 0,
          item.reason || '',
          entry.reason || '',
          entry.createdBy?.name || 'Unknown',
          entry.remarks || ''
        ])
      )
    ].map(row => row.map(val => `"${val}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demolished-tiles-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // React Select dark mode styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      fontSize: '14px',
      backgroundColor: darkMode ? '#374151' : base.backgroundColor,
      borderColor: darkMode ? '#4B5563' : base.borderColor,
      color: darkMode ? '#F3F4F6' : base.color,
      '&:hover': { borderColor: darkMode ? '#6B7280' : base.borderColor }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : base.backgroundColor,
      border: darkMode ? '1px solid #4B5563' : base.border
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? (darkMode ? '#4B5563' : '#EBF5FF')
        : state.isSelected
          ? (darkMode ? '#1F2937' : '#2563EB')
          : (darkMode ? '#374151' : base.backgroundColor),
      color: darkMode ? '#F3F4F6' : base.color,
      '&:active': { backgroundColor: darkMode ? '#1F2937' : base.backgroundColor }
    }),
    singleValue: (base) => ({ ...base, color: darkMode ? '#F3F4F6' : base.color }),
    input: (base) => ({ ...base, color: darkMode ? '#F3F4F6' : base.color }),
    placeholder: (base) => ({ ...base, color: darkMode ? '#9CA3AF' : base.color })
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            <FaTrashAlt className="inline mr-2 text-orange-600" />
            Demolished Tiles
          </h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Write off full damaged (unsellable) tiles with approval workflow
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className={`flex space-x-8 ${darkMode ? 'border-b border-gray-700' : ''}`}>
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-orange-500 text-orange-600'
                  : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaPlus className="mr-2" />
              Create Entry
            </button>
            <button
              onClick={() => { setActiveTab('history'); fetchDemolishHistory(); }}
              className={`${
                activeTab === 'history'
                  ? 'border-orange-500 text-orange-600'
                  : `border-transparent ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:border-gray-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <FaHistory className="mr-2" />
              History ({totalHistory})
            </button>
          </nav>
        </div>

        {/* Entry Form Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaMapMarkerAlt className="inline mr-1" />
                    Location *
                  </label>
                  <Select
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    options={locations.map(loc => ({
                      value: loc._id,
                      label: loc.name || loc.locationName
                    }))}
                    placeholder="Select location"
                    isClearable
                    className="text-sm"
                    styles={selectStyles}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaCalendarAlt className="inline mr-1" />
                    Date *
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                    required
                  />
                </div>
              </div>

              {/* Overall Reason */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaExclamationTriangle className="inline mr-1 text-yellow-500" />
                  Reason for Demolition *
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                  placeholder="e.g., Severely chipped, Cracked beyond use, Water damage..."
                  required
                />
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Tile
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaBox className="inline mr-1" />
                        Full Dmg Boxes
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        <FaCube className="inline mr-1" />
                        Full Dmg Pieces
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Available
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Item Reason
                      </th>
                      <th className={`border px-4 py-2 text-left text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <div
                            className={`flex items-center justify-between cursor-pointer p-2 rounded border border-dashed ${darkMode ? 'border-gray-500 hover:bg-gray-600' : 'border-gray-300 hover:bg-orange-50'}`}
                            onClick={() => {
                              setCurrentItemIndex(index);
                              setShowTilesModal(true);
                            }}
                          >
                            <span className={`text-sm ${darkMode ? 'text-gray-200' : ''}`}>
                              {item.tileName || 'Click to select tile'}
                            </span>
                            <FaSearch className={darkMode ? 'text-gray-500' : 'text-gray-400'} />
                          </div>
                          {item.tileDetails && (
                            <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.tileDetails.brand?.name} • {item.tileDetails.tilesCategory?.name}
                              {item.tileDetails.tilesPiecesPerBox ? ` • ${item.tileDetails.tilesPiecesPerBox} pcs/box` : ''}
                            </div>
                          )}
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            ref={el => boxRefs.current[index] = el}
                            type="number"
                            min="0"
                            max={item.availableFullDamagedBox}
                            value={item.demolishedDamagedBoxes}
                            onChange={e => handleItemChange(index, 'demolishedDamagedBoxes', e.target.value)}
                            onKeyDown={e => handleKeyDown(e, index)}
                            className={`w-24 px-2 py-1 border rounded text-sm text-center ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            disabled={!item.tileId}
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            type="number"
                            min="0"
                            max={item.availableFullDamagedPieces}
                            value={item.demolishedDamagedPieces}
                            onChange={e => handleItemChange(index, 'demolishedDamagedPieces', e.target.value)}
                            className={`w-24 px-2 py-1 border rounded text-sm text-center ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            disabled={!item.tileId}
                          />
                        </td>
                        <td className={`border px-4 py-2 text-center ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <span className={`text-sm font-medium ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            {item.availableFullDamagedBox}B / {item.availableFullDamagedPieces}P
                          </span>
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <input
                            type="text"
                            value={item.reason}
                            onChange={e => handleItemChange(index, 'reason', e.target.value)}
                            className={`w-full px-2 py-1 border rounded text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                            placeholder="Optional"
                          />
                        </td>
                        <td className={`border px-4 py-2 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            disabled={items.length === 1}
                            className={`p-1 rounded ${items.length === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-100 text-red-600'}`}
                          >
                            <FaMinus />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  onClick={addItem}
                  className={`mt-2 flex items-center gap-1 px-3 py-1 rounded text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                >
                  <FaPlus className="text-xs" /> Add Tile
                </button>
              </div>

              {/* Remarks */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaStickyNote className="inline mr-1" />
                  Remarks
                </label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                  rows={3}
                  placeholder="Additional notes (optional)"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all ${
                    submitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-orange-600 hover:bg-orange-700 text-white'
                  }`}
                >
                  <FaSave />
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className={`rounded-lg shadow-md ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* History Header with Filters */}
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm ${
                      showFilters 
                        ? 'bg-orange-100 text-orange-700' 
                        : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                    }`}
                  >
                    <FaFilter /> Filters
                  </button>
                  {(historyFilters.location || historyFilters.startDate || historyFilters.endDate || historyFilters.status) && (
                    <button
                      onClick={resetHistoryFilters}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs text-red-600 hover:bg-red-50"
                    >
                      <FaUndo /> Reset
                    </button>
                  )}
                </div>
                <button
                  onClick={exportToCSV}
                  disabled={demolishHistory.length === 0}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm ${
                    demolishHistory.length === 0
                      ? 'opacity-50 cursor-not-allowed bg-gray-200 text-gray-500'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  <FaDownload /> Export CSV
                </button>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className={`mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <Select
                    value={historyFilters.location ? locations.filter(l => l._id === historyFilters.location).map(l => ({ value: l._id, label: l.name }))[0] : null}
                    onChange={(v) => handleHistoryFilterChange('location', v?.value || null)}
                    options={locations.map(loc => ({ value: loc._id, label: loc.name }))}
                    placeholder="Location"
                    isClearable
                    styles={selectStyles}
                    className="text-sm"
                  />
                  <input
                    type="date"
                    value={historyFilters.startDate || ''}
                    onChange={(e) => handleHistoryFilterChange('startDate', e.target.value || null)}
                    className={`px-3 py-2 border rounded-md text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'border-gray-300'}`}
                    placeholder="Start date"
                  />
                  <input
                    type="date"
                    value={historyFilters.endDate || ''}
                    onChange={(e) => handleHistoryFilterChange('endDate', e.target.value || null)}
                    className={`px-3 py-2 border rounded-md text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'border-gray-300'}`}
                    placeholder="End date"
                  />
                  <Select
                    value={historyFilters.status ? { value: historyFilters.status, label: historyFilters.status.charAt(0).toUpperCase() + historyFilters.status.slice(1) } : null}
                    onChange={(v) => handleHistoryFilterChange('status', v?.value || null)}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' }
                    ]}
                    placeholder="Status"
                    isClearable
                    styles={selectStyles}
                    className="text-sm"
                  />
                </div>
              )}
            </div>

            {/* History Table */}
            <div className="overflow-x-auto">
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : demolishHistory.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FaFileAlt className="mx-auto text-4xl mb-3 opacity-40" />
                  <p className="text-lg font-medium">No demolition records found</p>
                  <p className="text-sm">Create a new entry from the "Create Entry" tab</p>
                </div>
              ) : (
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Location</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Items</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Qty</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {demolishHistory.map(entry => {
                      const totalBoxes = (entry.items || []).reduce((sum, i) => sum + (i.demolishedDamagedBoxes || 0), 0);
                      const totalPieces = (entry.items || []).reduce((sum, i) => sum + (i.demolishedDamagedPieces || 0), 0);
                      return (
                        <tr key={entry._id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            {entry.demolishedId}
                          </td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {formatDate(entry.demolishedDate)}
                          </td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {entry.location?.name || 'N/A'}
                          </td>
                          <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {entry.items?.length || 0} tile{(entry.items?.length || 0) !== 1 ? 's' : ''}
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {totalBoxes}B / {totalPieces}P
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(entry.status)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleViewDetails(entry)}
                                className="p-1.5 rounded hover:bg-blue-100 text-blue-600 transition-colors"
                                title="View Details"
                              >
                                <FaEye />
                              </button>
                              {entry.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApproval(entry._id, 'approved')}
                                    disabled={approving}
                                    className="p-1.5 rounded hover:bg-green-100 text-green-600 transition-colors"
                                    title="Approve"
                                  >
                                    <FaCheck />
                                  </button>
                                  <button
                                    onClick={() => handleApproval(entry._id, 'rejected')}
                                    disabled={approving}
                                    className="p-1.5 rounded hover:bg-red-100 text-red-600 transition-colors"
                                    title="Reject"
                                  >
                                    <FaBan />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Page {historyPage} of {totalPages} ({totalHistory} total)
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setHistoryPage(1)} disabled={historyPage === 1}
                    className={`p-1.5 rounded ${historyPage === 1 ? 'opacity-50' : 'hover:bg-gray-200'} ${darkMode ? 'text-gray-300' : ''}`}>
                    <FaAngleDoubleLeft />
                  </button>
                  <button onClick={() => setHistoryPage(p => Math.max(1, p - 1))} disabled={historyPage === 1}
                    className={`p-1.5 rounded ${historyPage === 1 ? 'opacity-50' : 'hover:bg-gray-200'} ${darkMode ? 'text-gray-300' : ''}`}>
                    <FaChevronLeft />
                  </button>
                  <button onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))} disabled={historyPage === totalPages}
                    className={`p-1.5 rounded ${historyPage === totalPages ? 'opacity-50' : 'hover:bg-gray-200'} ${darkMode ? 'text-gray-300' : ''}`}>
                    <FaChevronRight />
                  </button>
                  <button onClick={() => setHistoryPage(totalPages)} disabled={historyPage === totalPages}
                    className={`p-1.5 rounded ${historyPage === totalPages ? 'opacity-50' : 'hover:bg-gray-200'} ${darkMode ? 'text-gray-300' : ''}`}>
                    <FaAngleDoubleRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tiles Selection Modal */}
        {showTilesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowTilesModal(false)}>
            <div 
              className={`w-full max-w-2xl mx-4 rounded-xl shadow-2xl max-h-[80vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={e => e.stopPropagation()}
              onKeyDown={handleModalKeyDown}
            >
              <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Select Tile</h3>
                <button onClick={() => setShowTilesModal(false)} className="text-gray-400 hover:text-gray-600">
                  <FaTimes />
                </button>
              </div>
              
              <div className="p-4">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={tilesSearch}
                    onChange={(e) => { setTilesSearch(e.target.value); setTilesPage(1); }}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'border-gray-300'}`}
                    placeholder="Search by name, ID, brand, category..."
                  />
                </div>
              </div>

              <div className="overflow-y-auto flex-1 px-4 pb-4">
                {paginatedTiles.length === 0 ? (
                  <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No tiles found
                  </div>
                ) : (
                  <div className="space-y-1">
                    {paginatedTiles.map((tile, idx) => {
                      const locStock = selectedLocation 
                        ? tile.stock?.find(s => (s.location?._id || s.location) === selectedLocation.value)
                        : null;
                      const fullDmgBox = locStock?.fullDamagedBox || 0;
                      const fullDmgPcs = locStock?.fullDamagedPieces || 0;
                      
                      return (
                        <div
                          key={tile._id}
                          onClick={() => handleTileSelect(tile, currentItemIndex)}
                          className={`p-3 rounded-lg cursor-pointer transition-colors ${
                            idx === selectedTileIndex
                              ? (darkMode ? 'bg-orange-900/30 border border-orange-600' : 'bg-orange-50 border border-orange-300')
                              : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className={`font-medium text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                {tile.tilesName || tile.displayName}
                              </div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {tile.id} • {tile.brand?.name} • {tile.tilesCategory?.name}
                                {tile.tilesPiecesPerBox ? ` • ${tile.tilesPiecesPerBox} pcs/box` : ''}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-xs font-medium ${fullDmgBox > 0 || fullDmgPcs > 0 ? (darkMode ? 'text-orange-400' : 'text-orange-600') : (darkMode ? 'text-gray-500' : 'text-gray-400')}`}>
                                Full Dmg: {fullDmgBox}B / {fullDmgPcs}P
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Pagination */}
              {totalTilesPages > 1 && (
                <div className={`flex items-center justify-between p-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Page {tilesPage}/{totalTilesPages} ({filteredTiles.length} results)
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => setTilesPage(p => Math.max(1, p - 1))} disabled={tilesPage === 1}
                      className={`px-2 py-1 rounded text-xs ${tilesPage === 1 ? 'opacity-50' : 'hover:bg-gray-200'} ${darkMode ? 'text-gray-300' : ''}`}>
                      <FaChevronLeft />
                    </button>
                    <button onClick={() => setTilesPage(p => Math.min(totalTilesPages, p + 1))} disabled={tilesPage === totalTilesPages}
                      className={`px-2 py-1 rounded text-xs ${tilesPage === totalTilesPages ? 'opacity-50' : 'hover:bg-gray-200'} ${darkMode ? 'text-gray-300' : ''}`}>
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              )}

              <div className={`px-4 py-2 text-xs border-t ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                ↑↓ Navigate • Enter Select • Esc Close
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedEntry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowDetailsModal(false)}>
            <div 
              className={`w-full max-w-3xl mx-4 rounded-xl shadow-2xl max-h-[85vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
              onClick={e => e.stopPropagation()}
            >
              <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                  Demolition Details - {selectedEntry.demolishedId}
                </h3>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <FaTimes />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                    <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</div>
                    {getStatusBadge(selectedEntry.status)}
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                    <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</div>
                    <div className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {formatDate(selectedEntry.demolishedDate)}
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                    <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Location</div>
                    <div className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {selectedEntry.location?.name || 'N/A'}
                    </div>
                  </div>
                  <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                    <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Created By</div>
                    <div className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      <FaUser className="inline mr-1" />{selectedEntry.createdBy?.name || 'Unknown'}
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div className={`rounded-lg p-3 border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-yellow-700'}`}>
                    <FaExclamationTriangle className="inline mr-1" /> Reason
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {selectedEntry.reason}
                  </div>
                </div>

                {/* Items Table */}
                <div>
                  <h4 className={`text-sm font-bold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Items ({selectedEntry.items?.length || 0})</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full table-auto">
                      <thead>
                        <tr className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                          <th className={`px-3 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tile</th>
                          <th className={`px-3 py-2 text-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Full Dmg Boxes</th>
                          <th className={`px-3 py-2 text-center text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Full Dmg Pieces</th>
                          <th className={`px-3 py-2 text-left text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Reason</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                        {(selectedEntry.items || []).map((item, idx) => (
                          <tr key={idx} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                            <td className={`px-3 py-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                              <div className="text-sm font-medium">{item.tileId?.tilesName || item.tileId?.displayName || 'N/A'}</div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.tileId?.partNumber || ''}</div>
                            </td>
                            <td className={`px-3 py-2 text-center font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                              {item.demolishedDamagedBoxes || 0}
                            </td>
                            <td className={`px-3 py-2 text-center font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                              {item.demolishedDamagedPieces || 0}
                            </td>
                            <td className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {item.reason || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Remarks */}
                {selectedEntry.remarks && (
                  <div className={`rounded-lg p-3 border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <FaStickyNote className="inline mr-1" /> Remarks
                    </div>
                    <div className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {selectedEntry.remarks}
                    </div>
                  </div>
                )}

                {/* Approval Actions */}
                {selectedEntry.status === 'pending' && (
                  <div className={`flex gap-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                      onClick={() => handleApproval(selectedEntry._id, 'approved')}
                      disabled={approving}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      <FaCheck /> Approve
                    </button>
                    <button
                      onClick={() => handleApproval(selectedEntry._id, 'rejected')}
                      disabled={approving}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      <FaBan /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemolishedTiles;
